import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';

import { IBackendCartEntries, ILegacyCartEntry, IPicker } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { LoyaltyReward } from 'state/loyalty/types';
import { IPendingReorder } from 'state/order/types';
import { isOfferType } from 'state/order/utils';
import { normalizeCartEntries } from 'utils/cart-utils';
import { isDummyItemName } from 'utils/cart/is-dummy-item';
import { CartEntryType } from 'utils/cart/types';

type ReorderableCartEntry = IBackendCartEntries | ICartEntry;

const isMinimalCartEntry = (entry: ReorderableCartEntry): entry is ICartEntry =>
  'id' in entry && !('sanityId' in entry);

export function isComboMarker({ name, lineId }: { name?: string; lineId?: string }): boolean {
  if (!name) {
    // Loyalty markers have name null and lineId===loyalty
    // Other items might not have name, so we assume those aren't markers
    return lineId === 'loyalty';
  }
  return isDummyItemName(name);
}

/**
 * Updates a cart reward cart entry
 *
 * @param {IBackendCartEntries} cartEntry - The original cart entry.
 * @returns {IBackendCartEntries} The modified cart entry.
 */
export function maybeExtractRewardBenefitEntry(
  cartEntry: IBackendCartEntries
): IBackendCartEntries {
  const hasValidChildren = Array.isArray(cartEntry.children) && cartEntry.children.length > 0;

  // Reward items don't share same structure as ALC items.
  // Rewards items will always have 3 children.
  // 1st one is comboItemMarkerEntry ,
  // 2nd is comboSideMarkerEntry (used for NCR) ,
  // 3rd is rewardBenefitEntry which represents actual reward benefit comboSlot
  // which has a children and that children is the actual reward item.
  if (cartEntry.type === CartEntryType.comboSlot && hasValidChildren) {
    const foundEntry = cartEntry.children!.find(
      entry => entry.name && entry.name.toLowerCase() !== 'combo sides'
    );

    if (foundEntry) {
      return {
        ...foundEntry,
        url: `/menu/${foundEntry.type.toLowerCase()}-${foundEntry.sanityId}`,
      };
    }
  }

  if (hasValidChildren) {
    for (const entry of cartEntry.children!) {
      try {
        return maybeExtractRewardBenefitEntry(entry);
      } catch (_) {}
    }
  }

  throw new Error('No valid reward entry found');
}

/**
 * Updates a cart entry based on a loyalty reward.
 *
 * @param {IBackendCartEntries} cartEntry - The original cart entry.
 * @param {LoyaltyReward} reward - The loyalty reward.
 * @returns {IBackendCartEntries} The modified cart entry.
 */
export function maybeCreateCartEntryFromReward(
  cartEntry: IBackendCartEntries,
  reward: LoyaltyReward
): IBackendCartEntries {
  const incentive = reward.incentives?.[0];

  if (!incentive) {
    return cartEntry;
  }

  if (incentive._type === MenuObjectTypes.PICKER) {
    const userSelection = cartEntry.children?.find(child => child.lineId === 'loyalty');

    if (!userSelection) {
      return cartEntry;
    }

    const pickerOption = ((incentive as unknown) as IPicker).options.find(
      option => option.option._id === userSelection.sanityId
    );

    if (!pickerOption) {
      return cartEntry;
    }

    const benefitType =
      pickerOption.option._type === 'combo' ? CartEntryType.combo : CartEntryType.item;

    return {
      ...cartEntry,
      type: benefitType,
      sanityId: userSelection.sanityId,
    };
  }

  if (incentive._type === 'combo' || incentive._type === 'item') {
    return {
      ...cartEntry,
      sanityId: incentive._id,
      type: incentive._type === 'combo' ? CartEntryType.combo : CartEntryType.item,
    };
  }

  return cartEntry;
}

export const isCartEntryReorderable = (
  entry: ReorderableCartEntry,
  sanityIdsExcludedFromReordering?: EnableMenuReorderingAvailabilityOverrideVariation | null,
  isFoodware: (cartEntry: ILegacyCartEntry | IBackendCartEntries) => boolean = () => false
): boolean => {
  const entryId = isMinimalCartEntry(entry) ? entry.id : entry.sanityId;
  const isExcludedFromReordering = (sanityIdsExcludedFromReordering ?? []).includes(entryId);
  const isDonation =
    entry.isDonation || (isMinimalCartEntry(entry) && entry.type === MinimalCartEntryType.donation);
  const isExtra =
    entry.isExtra || (isMinimalCartEntry(entry) && entry.type === MinimalCartEntryType.extra);
  const isFoodwareEntry = !isMinimalCartEntry(entry) && isFoodware(entry);
  const isMalformedMinimalEntry =
    isMinimalCartEntry(entry) && (!entry.id.trim() || !entry.name.trim());
  const lineId = 'lineId' in entry ? entry.lineId : undefined;

  return (
    !isMalformedMinimalEntry &&
    !isComboMarker({ name: entry.name, lineId }) &&
    !isExcludedFromReordering &&
    !isDonation &&
    !isExtra &&
    !isFoodwareEntry
  );
};

export const maybeCreateCartEntriesFromOffer = (
  entry: IBackendCartEntries,
  sanityIdsExcludedFromReordering:
    | EnableMenuReorderingAvailabilityOverrideVariation
    | null
    | undefined,
  isFoodware: (cartEntry: ILegacyCartEntry | IBackendCartEntries) => boolean
): IBackendCartEntries[] => {
  const mappedCartEntries: IBackendCartEntries[] = [];

  for (const rootLevelCartEntry of entry.children ?? []) {
    const isComboSlot = rootLevelCartEntry.type === CartEntryType.comboSlot;

    if (isComboSlot) {
      // Add all valid comboSlotOptions
      for (const comboSlotCartEntry of rootLevelCartEntry.children ?? []) {
        if (
          isCartEntryReorderable(comboSlotCartEntry, sanityIdsExcludedFromReordering, isFoodware)
        ) {
          mappedCartEntries.push({
            ...comboSlotCartEntry,
            url: `/menu/${comboSlotCartEntry.type.toLowerCase()}-${comboSlotCartEntry.sanityId}`,
          } as IBackendCartEntries);
        }
      }

      continue;
    }

    // Regular root level entries should be added as is (generally these are offer's mainItem)
    if (isCartEntryReorderable(rootLevelCartEntry, sanityIdsExcludedFromReordering, isFoodware)) {
      mappedCartEntries.push({
        ...rootLevelCartEntry,
        url: `/menu/${rootLevelCartEntry.type.toLowerCase()}-${rootLevelCartEntry.sanityId}`,
      } as IBackendCartEntries);
    }
  }

  return mappedCartEntries;
};

interface IMakeOrderReorderable {
  order: IPendingReorder;
  sanityIdsExcludedFromReordering:
    | EnableMenuReorderingAvailabilityOverrideVariation
    | null
    | undefined;
  allRewardsMap: any;
  isUnifiedMenuEnabled: boolean;
  isFoodware: (cartEntry: ILegacyCartEntry | IBackendCartEntries) => boolean;
}

export const makeOrderReorderable = ({
  order,
  sanityIdsExcludedFromReordering,
  isFoodware,
  allRewardsMap,
  isUnifiedMenuEnabled,
}: IMakeOrderReorderable): IPendingReorder => {
  const mappedReorderCartEntries: IBackendCartEntries[] = [];

  for (const entry of order?.cart?.cartEntries ?? []) {
    if (!isCartEntryReorderable(entry, sanityIdsExcludedFromReordering, isFoodware)) {
      continue;
    }

    let mappedEntries: IBackendCartEntries[] = [];

    // Offers or Rewards
    if (isOfferType(entry.type as CartEntryType)) {
      // Checks if a reorder cart entry is a reward,
      // then replaces the cart entry with the reward benefit item.
      const reward = allRewardsMap?.[entry.sanityId];

      if (reward) {
        const rewardCartEntry = maybeCreateCartEntryFromReward(entry, reward);
        mappedEntries = [maybeExtractRewardBenefitEntry(rewardCartEntry)];
      } else {
        // If it's not a reward, it's an Offer
        // Offer can't be re-applied, so we spread all its children instead
        mappedEntries = maybeCreateCartEntriesFromOffer(
          entry,
          sanityIdsExcludedFromReordering,
          isFoodware
        );
      }
    } else {
      // Regular entries are preserved as is
      mappedEntries = [{ ...entry, url: `/menu/${entry.type.toLowerCase()}-${entry.sanityId}` }];
    }

    const entries = isUnifiedMenuEnabled ? mappedEntries : normalizeCartEntries(mappedEntries);

    mappedReorderCartEntries.push(...entries);
  }

  return {
    ...order,
    cart: {
      ...order.cart,
      reorderCartEntries: mappedReorderCartEntries,
    },
  };
};
